import { TableCell, TableRowHeader, TableRow, Button, IconButton } from '@/common'
import { UnitLabel } from '@/shared/config/config'
import { useCallback, useEffect, useState } from 'react'
import useI18n from '@/shared/hooks/useI18n'
import LocalizedNumberFlow from '@/components/LocalizednNumberFlow'
import { usePermissions } from '@/shared/hooks/usePermissions'
import CnCodeTag from '@/components/CnCodeTag'
import { TrashIcon } from '@/common/components/icons'
import { PrecursorMaterial, useDeletePrecursorMaterial } from '@/hooks/api/v2/precursorMaterials'
import { PrecursorEmissionReport } from '@/hooks/api/v2/precursorEmissionReports'
import useOrganizationDocuments from '@/shared/hooks/useOrganizationDocuments'
import { useReEvaluateActivityLevelValidationForMethodEmissionReport } from '@/hooks/api/emissionReports'

export default function InputPrecursorTableRow({
  isViewMode,
  precursor,
  emissionReportId,
  methodEmissionReportId,
  onEdit,
  precursorEmissionReport,
  installationId
}: {
  precursor: PrecursorMaterial
  precursorEmissionReport: PrecursorEmissionReport
  isViewMode: boolean
  emissionReportId: string
  methodEmissionReportId: string
  onEdit: (precursorMaterialId: string) => void
  installationId: string
}) {
  const { quantityInTonnes, internalId, proofFilePath } = precursor

  const {
    cnCode,
    goodsDescription,
    specificDirectEmbeddedEmissions,
    specificIndirectEmbeddedEmissions,
    precursorSupplier: { name: supplierName }
  } = precursorEmissionReport

  const { t } = useI18n()
  const amountInTonnes = quantityInTonnes
  const emissionInTonnes = specificDirectEmbeddedEmissions
  const indirectEmissionInTonnes = specificIndirectEmbeddedEmissions

  const { checkPermission } = usePermissions()

  const { mutateAsync: deletePrecursorMaterial, isPending: isRemovingPrecursor } =
    useDeletePrecursorMaterial(installationId)

  const { mutateAsync: reEvaluateActivityLevelValidationForMethodEmissionReport } =
    useReEvaluateActivityLevelValidationForMethodEmissionReport(
      emissionReportId,
      methodEmissionReportId
    )

  const onRemoveClick = useCallback(() => {
    deletePrecursorMaterial({ precursorMaterialId: precursor.id })
    reEvaluateActivityLevelValidationForMethodEmissionReport()
  }, [
    deletePrecursorMaterial,
    precursor.id,
    reEvaluateActivityLevelValidationForMethodEmissionReport
  ])

  const onEditClick = useCallback(() => {
    onEdit(precursor.id)
  }, [onEdit, precursor.id])

  const removeButton = (
    <IconButton
      disabled={isRemovingPrecursor}
      onClick={onRemoveClick}
      variant="txt-danger"
      icon={<TrashIcon />}
    />
  )

  const editButton = (
    <Button onClick={onEditClick} variant="txt-default" sizeType="sm">
      {t('common:actions.edit')}
    </Button>
  )

  const title = `${t('report:precursors.purchasedFrom', { name: supplierName })}`

  const canDeletePrecursor = checkPermission('precursor', 'delete')
  const canEditPrecursor = checkPermission('precursor', 'update')

  const hasEmission = emissionInTonnes !== 0
  const hasIndirectEmission = indirectEmissionInTonnes !== 0

  const { generateDownloadUrl } = useOrganizationDocuments()
  const [url, setUrl] = useState<string>('')
  useEffect(() => {
    const fetchUrl = async () => {
      if (proofFilePath) {
        const url = await generateDownloadUrl(proofFilePath)
        if (url) {
          setUrl(url)
        }
      }
    }
    if (!url) {
      fetchUrl()
    }
  }, [generateDownloadUrl, proofFilePath, url])

  return (
    <TableRow>
      <TableRowHeader className="text-left">{title}</TableRowHeader>
      <TableCell>
        <CnCodeTag cnCode={cnCode} />
      </TableCell>
      <TableCell>{goodsDescription}</TableCell>
      <TableCell>{!internalId ? '-' : internalId}</TableCell>
      <TableCell>
        {proofFilePath ? (
          <a href={url} download={proofFilePath} className="text-blue-500 hover:underline">
            {proofFilePath}
          </a>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell>
        {hasEmission ? (
          <>
            <LocalizedNumberFlow value={Number(emissionInTonnes || 0)} /> {UnitLabel.tCO2ePerT}
          </>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell>
        {hasIndirectEmission ? (
          <>
            <LocalizedNumberFlow value={Number(indirectEmissionInTonnes || 0)} />{' '}
            {UnitLabel.tCO2ePerT}
          </>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell>
        <LocalizedNumberFlow value={Number(amountInTonnes || 0)} /> {UnitLabel.t}
      </TableCell>
      <TableCell>
        <div className="flex gap-2 justify-end">
          {!isViewMode && canEditPrecursor && editButton}
          {!isViewMode && canDeletePrecursor && removeButton}
        </div>
      </TableCell>
    </TableRow>
  )
}
